/**
 * .NET biçim dizelerinin (DisplayFormat) tarayıcı tarafı yorumu.
 *
 * - Sayı: standart (N0..N6, F2, C2, P1, D3, G) ve özel (#,##0.##, 0.###, ###0, 0) kalıplar.
 * - Tarih: yyyy yy MM M dd d HH H hh h mm ss fff tt belirteçleri.
 * - Varsayılan biçim: Octo.Web.Core/Helpers/DataHelper.cs GetDisplayFormat.
 *
 * Gruplama/ondalık ayıracı `Intl.NumberFormat` ile kültürden alınır (varsayılan tr-TR).
 */

export const DEFAULT_CULTURE = 'tr-TR';

interface Separators {
  group: string;
  decimal: string;
  currency: string;
  currencyPrefix: boolean;
  percentPrefix: boolean;
}

const sepCache = new Map<string, Separators>();

function separators(culture: string): Separators {
  const cached = sepCache.get(culture);
  if (cached) return cached;

  let group = '.';
  let decimal = ',';
  let currency = '₺';
  let currencyPrefix = true;
  let percentPrefix = false;
  try {
    const parts = new Intl.NumberFormat(culture).formatToParts(1234567.89);
    group = parts.find((p) => p.type === 'group')?.value ?? group;
    decimal = parts.find((p) => p.type === 'decimal')?.value ?? decimal;

    const curCode = culture.toLowerCase().startsWith('tr')
      ? 'TRY'
      : culture.toLowerCase().startsWith('en-us')
        ? 'USD'
        : 'EUR';
    const curParts = new Intl.NumberFormat(culture, { style: 'currency', currency: curCode }).formatToParts(
      1,
    );
    const curIdx = curParts.findIndex((p) => p.type === 'currency');
    const intIdx = curParts.findIndex((p) => p.type === 'integer');
    if (curIdx >= 0) {
      currency = curParts[curIdx]?.value ?? currency;
      currencyPrefix = curIdx < intIdx;
    }

    const pctParts = new Intl.NumberFormat(culture, { style: 'percent' }).formatToParts(0.5);
    const pIdx = pctParts.findIndex((p) => p.type === 'percentSign');
    const iIdx = pctParts.findIndex((p) => p.type === 'integer');
    percentPrefix = pIdx >= 0 && pIdx < iIdx;
  } catch {
    /* Intl yoksa tr-TR varsayılanları */
  }

  const s: Separators = { group, decimal, currency, currencyPrefix, percentPrefix };
  sepCache.set(culture, s);
  return s;
}

/** Sayıyı `digits` ondalıkla, .NET gibi away-from-zero yuvarlar; string döner. */
function toFixedAwayFromZero(value: number, digits: number): string {
  const factor = 10 ** digits;
  const scaled = Math.abs(value) * factor;
  // Kayan nokta gürültüsü için küçük epsilon
  const rounded = Math.round(scaled + 1e-9);
  const s = (rounded / factor).toFixed(digits);
  return value < 0 && Number(s) !== 0 ? `-${s}` : s;
}

function groupInteger(intPart: string, group: string, size = 3): string {
  if (!group || intPart.length <= size) return intPart;
  let out = '';
  let count = 0;
  for (let i = intPart.length - 1; i >= 0; i--) {
    out = intPart[i] + out;
    count++;
    if (count % size === 0 && i > 0) out = group + out;
  }
  return out;
}

function renderFixed(
  value: number,
  minFrac: number,
  maxFrac: number,
  grouping: boolean,
  sep: Separators,
): string {
  const fixed = toFixedAwayFromZero(value, maxFrac);
  const negative = fixed.startsWith('-');
  const body = negative ? fixed.slice(1) : fixed;
  const [intRaw, fracRaw = ''] = body.split('.');
  let frac = fracRaw;
  // Fazla sıfırları minFrac'e kadar kırp
  while (frac.length > minFrac && frac.endsWith('0')) frac = frac.slice(0, -1);
  const intPart = grouping ? groupInteger(intRaw as string, sep.group) : (intRaw as string);
  const text = frac.length > 0 ? `${intPart}${sep.decimal}${frac}` : intPart;
  return negative ? `-${text}` : text;
}

interface CustomPattern {
  grouping: boolean;
  minInt: number;
  minFrac: number;
  maxFrac: number;
  percent: boolean;
  prefix: string;
  suffix: string;
}

function parseCustomPattern(format: string): CustomPattern | null {
  // Yalnızca pozitif bölümü (';' ile ayrılmışsa) destekle
  const section = (format.split(';')[0] ?? format).trim();
  const m = /^([^#0,.%]*)([#0,]*)(?:\.([#0]*))?(%?)([^#0,.%]*)$/.exec(section);
  if (!m) return null;
  const [, prefix = '', intPat = '', fracPat = '', pct = '', suffix = ''] = m;
  if (!intPat && !fracPat) return null;
  const grouping = intPat.includes(',');
  const intDigits = intPat.replace(/,/g, '');
  const minInt = (intDigits.match(/0/g) ?? []).length;
  const minFrac = (fracPat.match(/0/g) ?? []).length;
  const maxFrac = fracPat.length;
  return { grouping, minInt, minFrac, maxFrac, percent: pct === '%', prefix, suffix };
}

/**
 * .NET sayı biçimi: N0..N6, F0..F6, C0..C6, P0..P6, D1..D9, G ve özel kalıplar.
 * Tanınmayan biçimde kültür varsayılanı kullanılır.
 */
export function formatNumberDotNet(
  value: number | null | undefined,
  format: string | null | undefined,
  culture: string = DEFAULT_CULTURE,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const sep = separators(culture);
  const fmt = (format ?? '').trim();

  if (!fmt) return renderFixed(value, 0, 6, true, sep);

  const std = /^([A-Za-z])(\d{0,2})$/.exec(fmt);
  if (std) {
    const spec = (std[1] as string).toUpperCase();
    const digits = std[2] ? Number(std[2]) : undefined;
    switch (spec) {
      case 'N':
        return renderFixed(value, digits ?? 2, digits ?? 2, true, sep);
      case 'F':
        return renderFixed(value, digits ?? 2, digits ?? 2, false, sep);
      case 'C': {
        const body = renderFixed(Math.abs(value), digits ?? 2, digits ?? 2, true, sep);
        const signed = sep.currencyPrefix ? `${sep.currency}${body}` : `${body} ${sep.currency}`;
        return value < 0 ? `-${signed}` : signed;
      }
      case 'P': {
        const body = renderFixed(value * 100, digits ?? 2, digits ?? 2, true, sep);
        return sep.percentPrefix ? `%${body}` : `${body}%`;
      }
      case 'D': {
        const int = Math.trunc(Math.abs(value))
          .toString()
          .padStart(digits ?? 1, '0');
        return value < 0 ? `-${int}` : int;
      }
      case 'G':
      case 'R':
        return String(value).replace('.', sep.decimal);
      default:
        break;
    }
  }

  const custom = parseCustomPattern(fmt);
  if (custom) {
    const v = custom.percent ? value * 100 : value;
    let text = renderFixed(v, custom.minFrac, custom.maxFrac, custom.grouping, sep);
    if (custom.minInt > 0) {
      const negative = text.startsWith('-');
      const body = negative ? text.slice(1) : text;
      const decIdx = body.indexOf(sep.decimal);
      const intPart = decIdx >= 0 ? body.slice(0, decIdx) : body;
      const rest = decIdx >= 0 ? body.slice(decIdx) : '';
      const digitsOnly = intPart.replace(new RegExp(`\\${sep.group}`, 'g'), '');
      if (digitsOnly.length < custom.minInt) {
        const padded = digitsOnly.padStart(custom.minInt, '0');
        const regrouped = custom.grouping ? groupInteger(padded, sep.group) : padded;
        text = `${negative ? '-' : ''}${regrouped}${rest}`;
      }
    }
    return `${custom.prefix}${text}${custom.percent ? '%' : ''}${custom.suffix}`;
  }

  return renderFixed(value, 0, 6, true, sep);
}

// ---------------------------------------------------------------------------
// Tarih
// ---------------------------------------------------------------------------

const pad = (n: number, w: number) => String(n).padStart(w, '0');

/**
 * .NET özel tarih biçimi. Desteklenen belirteçler:
 * yyyy yy MM M dd d HH H hh h mm m ss s fff ff f tt t
 * Tek tırnak içindeki metin aynen yazılır.
 */
export function formatDateDotNet(value: Date | null | undefined, format: string): string {
  if (!value || Number.isNaN(value.getTime())) return '';
  const d = value;
  const h24 = d.getHours();
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const ampm = h24 < 12 ? 'AM' : 'PM';

  let out = '';
  let i = 0;
  const len = format.length;
  while (i < len) {
    const ch = format[i] as string;

    if (ch === "'" || ch === '"') {
      const close = format.indexOf(ch, i + 1);
      const end = close === -1 ? len : close;
      out += format.slice(i + 1, end);
      i = close === -1 ? len : close + 1;
      continue;
    }
    if (ch === '\\' && i + 1 < len) {
      out += format[i + 1];
      i += 2;
      continue;
    }

    let run = 1;
    while (i + run < len && format[i + run] === ch) run++;

    switch (ch) {
      case 'y':
        out +=
          run >= 4
            ? pad(d.getFullYear(), 4)
            : run === 3
              ? pad(d.getFullYear(), 3)
              : run === 2
                ? pad(d.getFullYear() % 100, 2)
                : String(d.getFullYear() % 100);
        break;
      case 'M':
        out += run >= 2 ? pad(d.getMonth() + 1, 2) : String(d.getMonth() + 1);
        break;
      case 'd':
        out += run >= 2 ? pad(d.getDate(), 2) : String(d.getDate());
        break;
      case 'H':
        out += run >= 2 ? pad(h24, 2) : String(h24);
        break;
      case 'h':
        out += run >= 2 ? pad(h12, 2) : String(h12);
        break;
      case 'm':
        out += run >= 2 ? pad(d.getMinutes(), 2) : String(d.getMinutes());
        break;
      case 's':
        out += run >= 2 ? pad(d.getSeconds(), 2) : String(d.getSeconds());
        break;
      case 'f': {
        const ms = pad(d.getMilliseconds(), 3);
        out += ms.slice(0, Math.min(run, 3)).padEnd(run, '0');
        break;
      }
      case 't':
        out += run >= 2 ? ampm : ampm[0];
        break;
      default:
        out += ch.repeat(run);
    }
    i += run;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Varsayılan biçim (DataHelper.GetDisplayFormat)
// ---------------------------------------------------------------------------

const INT_TYPES = new Set(['bigint', 'int', 'smallint', 'tinyint']);
const DECIMAL_TYPES = new Set(['decimal', 'money', 'smallmoney', 'float', 'real']);
const DATE_TYPES = new Set(['date', 'datetime', 'datetime2', 'datetimeoffset', 'smalldatetime']);

/**
 * DataHelper.GetDisplayFormat karşılığı:
 *   date → dd.MM.yyyy
 *   datetime2 → dd.MM.yyyy HH:mm:ss
 *   datetime / smalldatetime / datetimeoffset → dd.MM.yyyy HH:mm
 *   decimal / money / smallmoney / float / real → #,##0.###### (scale verilirse #,##0.{0×scale})
 *   bigint / int / smallint / tinyint → ###0
 *   diğer → ''
 *
 * Not: C# tarafında scale kullanılmaz; `scale` parametresi React tarafı için
 * isteğe bağlı bir iyileştirmedir (null/undefined → C# ile birebir).
 */
export function defaultDisplayFormat(dbTypeName: string | null | undefined, scale?: number | null): string {
  const t = (dbTypeName ?? '').toLowerCase();
  if (!t) return '';
  if (t === 'date') return 'dd.MM.yyyy';
  if (t === 'datetime2') return 'dd.MM.yyyy HH:mm:ss';
  if (DATE_TYPES.has(t)) return 'dd.MM.yyyy HH:mm';
  if (DECIMAL_TYPES.has(t)) {
    if (scale !== null && scale !== undefined && scale >= 0) {
      return scale === 0 ? '#,##0' : `#,##0.${'0'.repeat(scale)}`;
    }
    return '#,##0.######';
  }
  if (INT_TYPES.has(t)) return '###0';
  return '';
}

/** Alanın kendi DisplayFormat'ı varsa onu, yoksa tip varsayılanını döner. */
export function resolveDisplayFormat(
  displayFormat: string | null | undefined,
  dbTypeName: string | null | undefined,
  scale?: number | null,
): string {
  return displayFormat && displayFormat.trim() ? displayFormat : defaultDisplayFormat(dbTypeName, scale);
}
